import { Component, HostListener, OnInit } from '@angular/core';
import { WebService } from '../shared/services/web.service';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import cloneDeep from 'lodash/cloneDeep';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { fadeIn } from '../../app/shared/animation/animation.component';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import '../../../quill.config';

interface DataItem {
  newsletter_id: number;
  title: string;
  checked: boolean;
  banner_color: string;
  text_color?: string;
  description?: string;
  for_all: number;
  created: string;
}

interface ColumnItem {
  name: string;
  sortOrder: NzTableSortOrder | null;
  sortFn: NzTableSortFn<DataItem> | null;
  sortDirections: NzTableSortOrder[];
}

@Component({
  selector: 'newsletter',
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.css'],
  animations: [fadeIn]
})
export class NewsletterComponent implements OnInit {
  newsletterForm: FormGroup;
  metadata: any;
  newsletterData: any[];
  loadingFlag: boolean;
  updateMode: boolean;
  rawUpdateData: any;
  checkedData: any[];
  isIndeterminate: boolean;
  allChecked: boolean;
  anyChecked: boolean;
  iconVisible: boolean;
  newsletterFormVisible: boolean;
  newsletterDataRaw: any;
  updateNewsletterId: any;
  isLoading: boolean = false;
  isArchived: boolean = false;
  listOfColumns: ColumnItem[] = [
    {
      name: 'Title',
      sortOrder: null,
      sortFn: (a: DataItem, b: DataItem) =>
        a.title.localeCompare(b.title),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      name: 'Description',
      sortOrder: null,
      sortFn: (a: DataItem, b: DataItem) => {
        const aDesc = a.description || '';
        const bDesc = b.description || '';
        return aDesc.localeCompare(bDesc);
      },
      sortDirections: ['ascend', 'descend', null],
    },
    {
      name: 'Banner Color',
      sortOrder: null,
      sortFn: null,
      sortDirections: [],
    },
    {
      name: 'Text Color',
      sortOrder: null,
      sortFn: null,
      sortDirections: [],
    },
    {
      name: 'Created',
      sortOrder: null,
      sortFn: (a: DataItem, b: DataItem) => {
        const dateA = new Date(a.created || 0).getTime();
        const dateB = new Date(b.created || 0).getTime();
        return dateA - dateB;
      },
      sortDirections: ['ascend', 'descend', null],
    },
    {
      name: 'Actions',
      sortOrder: null,
      sortFn: null,
      sortDirections: [],
    },
  ];
  sortOrder: NzTableSortOrder | null;
  sortFn: NzTableSortFn<DataItem> | null;
  submitFlag: boolean = true;
  showLoad: boolean;
  filterVisible: boolean;
  archived: any;
  appliedFilters: any[];
  searchTerm: string;

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image', 'video']
    ]
  };

  confirmModal?: NzModalRef;
  showConfirm(): void {
    this.confirmModal = this.modal.confirm({
      nzTitle: 'All unsaved changes will be lost. Do you wish to proceed with closing the form?',
      nzContent: '',
      nzCentered: true,
      nzOnOk: () => {
        this.newsletterFormVisible = false;
      },
    });
  }

  originalFormValue: any;
  checkFormState() {
    if (this.isFormNotEmpty()) {
      this.showConfirm();
      return;
    }
    this.newsletterFormVisible = false;
  }

  setOriginalFormValue(): void {
    this.originalFormValue = { ...this.newsletterForm.value };
  }

  hasFormChanged(): boolean {
    if (!this.originalFormValue) {
      return false;
    }

    const formControls = this.newsletterForm.controls;
    for (const controlName in formControls) {
      if (formControls.hasOwnProperty(controlName)) {
        const originalValue = this.originalFormValue[controlName];
        const currentValue = formControls[controlName].value;
        if (originalValue != currentValue) {
          return true;
        }
      }
    }
    return false;
  }

  isFormNotEmpty(): boolean {
    if (this.newsletterForm.controls) {
      if (this.hasFormChanged()) return true;
      else return false;
    } else {
      for (const controlName in this.newsletterForm.controls) {
        if (this.newsletterForm.controls.hasOwnProperty(controlName)) {
          const control = this.newsletterForm.get(controlName);
          if (control && control.value !== null && control.value !== '') {
            return true;
          }
        }
      }
      return false;
    }
  }

  constructor(
    private webapi: WebService,
    private fb: FormBuilder,
    private notification: NzNotificationService,
    private modal: NzModalService
  ) {
    this.newsletterForm = this.fb.group({
      title: [null, [Validators.required]],
      content: [null, [Validators.required]],
      banner_color: ['#F1EDEB'],
      text_color: ['#000000'],
      description: [null, [Validators.required]],
    });
    this.newsletterData = [];
    this.getNewsletterList({ filters: [] });
  }

  ngOnInit() {}

  getNewsletterList({ filters }): void {
    this.showLoad = true;
    this.webapi
      .requestJson('getNewsletter', {
        Filters: filters.map((file) => {
          return {
            Key: file.key,
            Values: file.values,
          };
        }),
      })
      .subscribe(
        (data) => {
          let response = JSON.parse(data.body);
          this.newsletterData = [...response.data];
          this.newsletterData.forEach((nl) => {
            nl.checked = false;
          });
          this.newsletterDataRaw = cloneDeep(this.newsletterData);
          this.showLoad = false;
        },
        (error) => {
          console.log('ERROR', error);
          this.showLoad = false;
          this.createNotification('error', '', 'An error occurred. Please try again later.');
        }
      );
  }

  closeDetail() {
    this.iconVisible = false;
  }

  openNewsletterForm(): void {
    this.resetNewsletterForm();
    this.newsletterFormVisible = true;
    this.updateMode = false;
  }

  closeNewsletterForm(): void {
    this.resetNewsletterForm();
    this.newsletterData = cloneDeep(this.newsletterDataRaw);
    this.newsletterFormVisible = false;
  }

  fillNewsletterForm(data) {
    this.openNewsletterForm();
    this.newsletterForm.patchValue({
      title: data.title,
      content: data.content,
      banner_color: data.banner_color || '#F1EDEB',
      text_color: data.text_color || '#000000',
      description: data.description || null,
    });
    this.updateMode = true;
    this.updateNewsletterId = data.newsletter_id;
    this.rawUpdateData = { ...data };
    this.setOriginalFormValue();
  }

  resetNewsletterForm(): void {
    this.newsletterForm.reset();
    this.newsletterForm.patchValue({ 
      banner_color: '#F1EDEB',
      text_color: '#000000',
      description: null,
    });
    this.updateMode = false;
    this.updateNewsletterId = undefined;
    this.rawUpdateData = {};
    this.setOriginalFormValue();
  }

  resetUpdateForm(): void {
    this.newsletterForm.patchValue({
      title: this.rawUpdateData.title,
      content: this.rawUpdateData.content,
      banner_color: this.rawUpdateData.banner_color || '#F1EDEB',
      text_color: this.rawUpdateData.text_color || '#000000',
      description: this.rawUpdateData.description || null,
    });
  }

  onType() {
    console.log(this.newsletterForm.value);
  }

  submitNewsletter() {
    this.isArchived = false;
    this.archived = false;
    this.isLoading = true;
    let obj = {
      ...this.newsletterForm.value,
      for_all: 0, // Default to false when creating
    };
    this.webapi.requestJson('addNewsletter', obj).subscribe(
      (data) => {
        var response = JSON.parse(data.body);
        this.isLoading = false;
        this.createNotification('success', '', response['message']);
        this.applyFilters();
        this.closeNewsletterForm();
      },
      (error) => {
        this.isLoading = false;
        this.createNotification('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  updateNewsletter(): void {
    this.isLoading = true;
    let updateObj = {
      newsletter_id: this.updateNewsletterId,
      ...this.newsletterForm.value,
      for_all: this.rawUpdateData.for_all || 0, // Preserve existing for_all value
    };

    this.webapi.requestJson('updateNewsletter', updateObj).subscribe(
      (data) => {
        var response = JSON.parse(data.body);
        this.isLoading = false;
        this.createNotification('success', '', response['message']);
        this.getNewsletterList({ filters: [] });
        this.closeNewsletterForm();
      },
      (error) => {
        this.isLoading = false;
        this.createNotification('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.hasFormChanged()) {
      $event.preventDefault();
      $event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    }
  }

  createNotification(type, title, msg): void {
    this.notification.create(type, title, msg);
  }

  checkAll(value: boolean): void {
    this.newsletterData.forEach((data) => {
      data.checked = value;
    });
    this.refreshStatus();
  }

  refreshStatus(): void {
    this.checkedData = [];
    this.newsletterData.forEach((element) => {
      if (element.checked) {
        this.checkedData.push(element.newsletter_id);
      }
    });
    this.anyChecked = this.newsletterData.some((e) => e.checked);
    this.allChecked = this.newsletterData.every((e) => e.checked);
  }

  deleteNewsletter(newsletterId: any = null, multi = false): void {
    this.showLoad = true;
    let obj = {
      newsletter_id: [newsletterId],
    };
    if (multi) {
      obj.newsletter_id = [];
      obj.newsletter_id = this.checkedData;
    }
    this.webapi.requestJson('deleteNewsletter', obj).subscribe(
      (data) => {
        var response = JSON.parse(data.body);
        this.showLoad = false;
        this.createNotification('success', '', 'Newsletter deleted successfully');
        this.getNewsletterList({ filters: [] });
        this.refreshStatus();
      },
      (error) => {
        this.showLoad = false;
        this.createNotification('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  onKeyUp(searchTerm: string) {
    this.searchTerm = searchTerm;
    if (searchTerm === '') {
      this.newsletterData = cloneDeep(this.newsletterDataRaw);
    } else {
      this.newsletterData = this.newsletterDataRaw.filter((item) => {
        return item.title.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    this.refreshStatus();
  }

  resetSearch() {
    this.searchTerm = '';
    this.newsletterData = cloneDeep(this.newsletterDataRaw);
    this.refreshStatus();
  }

  clickFilter() {
    this.filterVisible = false;
  }

  applyFilters() {
    this.filterVisible = false;
    let filters = [];
    if (this.archived) {
      filters.push({ key: 'status', values: ['2'] });
    }
    this.getNewsletterList({ filters: filters });
  }

  onPopoverVisibleChange(visible: boolean) {
    if (!visible) {
      this.archived = this.isArchived;
    }
  }
}

